import { useContext, useRef, useState } from "react";
import { FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";
import {
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
} from "react-icons/md";
import LogicContext from "../../Context/LogicContext";
import { DocumentDeleteModal, ModalShell, PagerButton } from "./EmployeeDocumentsTab";

// Static option lists until the employee endpoints exist.
const DEGREES = ["OND", "HND", "B.Sc.", "B.Eng.", "M.Sc.", "MBA", "Ph.D.", "Other"];
const FIELDS_OF_STUDY = [
  "Computer Science",
  "Engineering",
  "Accounting",
  "Business Administration",
  "Economics",
  "Law",
  "Other",
];
const INSTITUTIONS = [
  "University of Lagos",
  "University of Ibadan",
  "Covenant University",
  "University of Nigeria, Nsukka",
  "Ahmadu Bello University",
  "Other",
];

const PAGE_SIZES = [5, 10, 20];

const SAMPLE_EDUCATION = [
  { degree: "B.Sc.", fieldOfStudy: "Computer Science", institution: "University of Lagos", passingYear: "2018", result: "Second Class Upper", remark: "-" },
  { degree: "M.Sc.", fieldOfStudy: "Engineering", institution: "Covenant University", passingYear: "2021", result: "Distinction", remark: "Part time" },
].map((record, index) => ({ ...record, id: index + 1 }));

const EMPTY_FORM = { degree: "", fieldOfStudy: "", institution: "", passingYear: "", result: "", remark: "" };

function SelectField({ label, value, options, required, onChange }) {
  return (
    <>
      <label className="mt-4 block text-xs font-medium text-gray-800">
        {label}
        {required ? "*" : ""}
      </label>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 h-10 w-full rounded-md border-0 bg-slate-100 px-3 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        <option value="">Select...</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </>
  );
}

function TextField({ label, value, required, maxLength, inputMode, onChange }) {
  return (
    <>
      <label className="mt-4 block text-xs font-medium text-gray-800">
        {label}
        {required ? "*" : ""}
      </label>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        maxLength={maxLength}
        inputMode={inputMode}
        placeholder="Input Text"
        className="mt-1 h-10 w-full rounded-md bg-slate-100 px-3 text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
    </>
  );
}

function EducationFormModal({ mode, initial, onSubmit, onClose }) {
  const [form, setForm] = useState(
    initial ? { ...initial, remark: initial.remark === "-" ? "" : initial.remark } : EMPTY_FORM
  );
  const [error, setError] = useState("");

  function setField(field) {
    return (value) => {
      setForm((current) => ({ ...current, [field]: value }));
      setError("");
    };
  }

  function handleSubmit(event) {
    event.preventDefault();
    const year = form.passingYear.trim();
    const result = form.result.trim();
    if (!form.degree) return setError("Degree is required.");
    if (!form.fieldOfStudy) return setError("Field of study is required.");
    if (!form.institution) return setError("Institution is required.");
    if (!/^\d{4}$/.test(year) || Number(year) < 1950 || Number(year) > new Date().getFullYear() + 6) {
      return setError("Passing year must be a valid 4 digit year.");
    }
    if (!result) return setError("Result is required.");
    onSubmit({ ...form, passingYear: year, result, remark: form.remark.trim() });
  }

  return (
    <ModalShell open onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className="px-6 pt-6">
          <h2 className="text-xl font-bold text-gray-900">{mode === "edit" ? "Edit Education" : "Add Education"}</h2>
          <SelectField label="Degree" required value={form.degree} options={DEGREES} onChange={setField("degree")} />
          <SelectField
            label="Field of study"
            required
            value={form.fieldOfStudy}
            options={FIELDS_OF_STUDY}
            onChange={setField("fieldOfStudy")}
          />
          <SelectField
            label="Institution"
            required
            value={form.institution}
            options={INSTITUTIONS}
            onChange={setField("institution")}
          />
          <TextField
            label="Passing Year"
            required
            maxLength={4}
            inputMode="numeric"
            value={form.passingYear}
            onChange={setField("passingYear")}
          />
          <TextField label="Result" required maxLength={100} value={form.result} onChange={setField("result")} />
          <TextField label="Remark" maxLength={200} value={form.remark} onChange={setField("remark")} />
          {error ? <p className="mt-3 text-xs text-red-600">{error}</p> : null}
        </div>
        <div className="mt-5 flex items-center justify-between border-t border-gray-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-md bg-slate-100 px-5 text-xs font-medium text-gray-700 hover:bg-slate-200"
          >
            Cancel
          </button>
          <button type="submit" className="h-9 rounded-md bg-blue-600 px-5 text-xs font-medium text-white hover:bg-blue-700">
            {mode === "edit" ? "Save changes" : "Save"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function EducationViewModal({ record, employeeName, onClose }) {
  const rows = [
    ["Degree", record.degree],
    ["Field of Study", record.fieldOfStudy],
    ["Institution", record.institution],
    ["Passing Year", record.passingYear],
    ["Result", record.result],
    ["Remark", record.remark || "-"],
  ];
  return (
    <ModalShell open onClose={onClose}>
      <div className="p-6">
        <h2 className="text-xl font-bold text-gray-900">{employeeName} Education Details</h2>
        <dl className="mt-6 space-y-2.5 text-xs">
          {rows.map(([label, value]) => (
            <div key={label} className="grid grid-cols-[110px_1fr] gap-2">
              <dt className="text-gray-500">{label}</dt>
              <dd className="break-words font-semibold text-gray-900">{value}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-8 flex justify-end">
          <button type="button" onClick={onClose} className="h-9 rounded-md bg-blue-600 px-5 text-xs font-medium text-white hover:bg-blue-700">
            Close
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function EmployeeEducationTab({ employeeName }) {
  const logicContext = useContext(LogicContext);
  const [records, setRecords] = useState(SAMPLE_EDUCATION);
  const [modal, setModal] = useState(null); // { kind: "add" | "edit" | "view" | "delete", record? }
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [page, setPage] = useState(1);
  const nextId = useRef(SAMPLE_EDUCATION.length + 1);

  const pageCount = Math.max(1, Math.ceil(records.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const rows = records.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const firstIndex = (currentPage - 1) * pageSize;

  function handleAdd(values) {
    setRecords((current) => [...current, { ...values, id: nextId.current++, remark: values.remark || "-" }]);
    setModal(null);
    logicContext.showToast("Education added successfully", "success");
  }

  function handleEdit(values) {
    const targetId = modal.record.id;
    setRecords((current) =>
      current.map((record) => (record.id === targetId ? { ...record, ...values, remark: values.remark || "-" } : record))
    );
    setModal(null);
    logicContext.showToast("Education updated successfully", "success");
  }

  function handleDelete() {
    const targetId = modal.record.id;
    setRecords((current) => current.filter((record) => record.id !== targetId));
    setModal(null);
    logicContext.showToast("Education deleted successfully", "success");
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Education</h2>
        <button
          type="button"
          onClick={() => setModal({ kind: "add" })}
          className="flex items-center gap-1 border-b border-blue-500 pb-0.5 text-xs font-medium text-blue-500"
        >
          <FiPlus className="h-3.5 w-3.5" />
          Add New
        </button>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[760px] text-left text-xs">
          <thead>
            <tr className="bg-slate-100 text-gray-800">
              <th className="w-12 rounded-l-md px-3 py-3 font-semibold">#</th>
              <th className="px-3 py-3 font-semibold">Degree</th>
              <th className="px-3 py-3 font-semibold">Field of Study</th>
              <th className="px-3 py-3 font-semibold">Institution</th>
              <th className="px-3 py-3 font-semibold">Passing Year</th>
              <th className="px-3 py-3 font-semibold">Result</th>
              <th className="px-3 py-3 font-semibold">Remark</th>
              <th className="rounded-r-md px-3 py-3 text-center font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-10 text-center text-sm text-gray-500">
                  No education records yet.
                </td>
              </tr>
            ) : (
              rows.map((record, index) => (
                <tr
                  key={record.id}
                  onClick={() => setModal({ kind: "view", record })}
                  className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-slate-50"
                >
                  <td className="px-3 py-4 text-gray-800">{firstIndex + index + 1}</td>
                  <td className="px-3 py-4 font-medium text-gray-900">{record.degree}</td>
                  <td className="px-3 py-4 text-gray-800">{record.fieldOfStudy}</td>
                  <td className="px-3 py-4 text-gray-800">{record.institution}</td>
                  <td className="px-3 py-4 text-gray-800">{record.passingYear}</td>
                  <td className="px-3 py-4 text-gray-800">{record.result}</td>
                  <td className="px-3 py-4 text-gray-800">{record.remark}</td>
                  <td className="px-3 py-4" onClick={(event) => event.stopPropagation()}>
                    <div className="flex items-center justify-center gap-3">
                      <button
                        type="button"
                        onClick={() => setModal({ kind: "edit", record })}
                        aria-label={`Edit ${record.degree}`}
                        className="text-gray-700 hover:text-blue-500"
                      >
                        <FiEdit2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setModal({ kind: "delete", record })}
                        aria-label={`Delete ${record.degree}`}
                        className="rounded bg-red-50 p-1.5 text-red-500 hover:bg-red-100"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs text-gray-600">
        <p>{records.length} record(s)</p>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <label className="flex items-center gap-2">
            Rows per page
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
              className="h-8 w-16 rounded border border-gray-200 bg-slate-50 px-2 text-xs text-gray-800 focus:outline-none"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <span>
            Page {currentPage} of {pageCount}
          </span>
          <div className="flex items-center">
            <PagerButton label="First page" onClick={() => setPage(1)} disabled={currentPage === 1}>
              <MdKeyboardDoubleArrowLeft className="h-4 w-4" />
            </PagerButton>
            <PagerButton label="Previous page" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1}>
              <MdKeyboardArrowLeft className="h-4 w-4" />
            </PagerButton>
            <PagerButton label="Next page" onClick={() => setPage(currentPage + 1)} disabled={currentPage === pageCount}>
              <MdKeyboardArrowRight className="h-4 w-4" />
            </PagerButton>
            <PagerButton label="Last page" onClick={() => setPage(pageCount)} disabled={currentPage === pageCount}>
              <MdKeyboardDoubleArrowRight className="h-4 w-4" />
            </PagerButton>
          </div>
        </div>
      </div>

      {modal?.kind === "add" ? <EducationFormModal mode="create" onSubmit={handleAdd} onClose={() => setModal(null)} /> : null}
      {modal?.kind === "edit" ? (
        <EducationFormModal mode="edit" initial={modal.record} onSubmit={handleEdit} onClose={() => setModal(null)} />
      ) : null}
      {modal?.kind === "view" ? (
        <EducationViewModal record={modal.record} employeeName={employeeName} onClose={() => setModal(null)} />
      ) : null}
      {modal?.kind === "delete" ? <DocumentDeleteModal onConfirm={handleDelete} onClose={() => setModal(null)} /> : null}
    </>
  );
}

export default EmployeeEducationTab;
